import type { ReactNode } from 'react';
import { Navigate, NavLink, useParams } from 'react-router-dom';
import { useApp } from '../lib/contexto';
import { DispositivosConectados } from './DispositivosConectados';
import { SeccionBot } from './configuracion/SeccionBot';
import { SeccionCuentas } from './configuracion/SeccionCuentas';
import { SeccionDescuentos } from './configuracion/SeccionDescuentos';
import { SeccionEquipo } from './configuracion/SeccionEquipo';
import { SeccionImpuestos } from './configuracion/SeccionImpuestos';
import { SeccionSemaforo } from './configuracion/SeccionSemaforo';

interface Seccion {
  ruta: string;
  texto: string;
  ver: (negocioId: string) => ReactNode;
}

const GRUPOS: { titulo: string; secciones: Seccion[] }[] = [
  {
    titulo: 'El dinero',
    secciones: [
      { ruta: 'cuentas', texto: 'Cuentas', ver: (id) => <SeccionCuentas negocioId={id} /> },
      { ruta: 'impuestos', texto: 'Impuestos', ver: (id) => <SeccionImpuestos negocioId={id} /> },
      { ruta: 'descuentos', texto: 'Descuentos', ver: (id) => <SeccionDescuentos negocioId={id} /> },
    ],
  },
  {
    titulo: 'El negocio',
    secciones: [
      { ruta: 'equipo', texto: 'Equipo', ver: (id) => <SeccionEquipo negocioId={id} /> },
      { ruta: 'dispositivos', texto: 'Dispositivos conectados', ver: (id) => <DispositivosConectados negocioId={id} /> },
      { ruta: 'bot', texto: 'Bot de Telegram', ver: (id) => <SeccionBot negocioId={id} /> },
      { ruta: 'semaforo', texto: 'Semáforo de formalización', ver: (id) => <SeccionSemaforo negocioId={id} /> },
    ],
  },
];

/** El cajero y el contador ven solo quiénes están en el equipo, sin poder cambiar nada. */
const GRUPOS_SOLO_LECTURA: { titulo: string; secciones: Seccion[] }[] = [
  {
    titulo: 'El negocio',
    secciones: [{ ruta: 'equipo', texto: 'Equipo', ver: (id) => <SeccionEquipo negocioId={id} soloLectura /> }],
  },
];

/** El menú de secciones a la izquierda y la sección elegida a todo el ancho; el dueño las ve todas. */
export function Configuracion() {
  const { negocio } = useApp();
  const { seccion } = useParams();
  if (!negocio) return null;
  const grupos = negocio.rol === 'propietario' ? GRUPOS : GRUPOS_SOLO_LECTURA;
  const actual = grupos.flatMap((g) => g.secciones).find((s) => s.ruta === seccion);
  if (!actual) return <Navigate to={`/configuracion/${grupos[0]?.secciones[0]?.ruta ?? 'equipo'}`} replace />;

  return (
    <div className="grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)]">
      <nav aria-label="Secciones de ajustes">
        <h1 className="mb-3 text-titulo font-extrabold">Ajustes</h1>
        {grupos.map((g) => (
          <div key={g.titulo} className="mb-2">
            <p className="text-muted px-3 pt-2 pb-1 text-etiqueta font-bold uppercase tracking-wide">{g.titulo}</p>
            <div className="flex flex-wrap gap-1 lg:flex-col">
              {g.secciones.map((s) => (
                <NavLink
                  key={s.ruta}
                  to={`/configuracion/${s.ruta}`}
                  data-e2e={`config-${s.ruta}`}
                  className={({ isActive }) =>
                    `rounded-control px-3 py-2 font-semibold ${isActive ? 'bg-warning/20 font-extrabold' : 'hover:bg-surface'}`
                  }
                >
                  {s.texto}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="min-w-0">{actual.ver(negocio.negocioId)}</div>
    </div>
  );
}
